import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Button, Card, IconButton, Switch, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { When } from 'react-if';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

import Loading from '../../../../components/loading';
import NoData from '../../../../components/no-data';
import AreYouSureModal from '../../../../components/are-you-sure-modal';
import { UnifiedFilterBar, FilterField } from '../../../../components/shared/UnifiedFilterBar';
import { ScheduledReportService } from '../../../../services/scheduledReport.service';
import { ScheduledReportTypes } from '../../../../types/scheduledReport.types';
import {
    SOURCE_OPTIONS, CADENCE_OPTIONS,
    SOURCE_LABELS, WINDOW_LABELS, CADENCE_LABELS, formatInTimezone,
} from '../../../../constants/scheduledReports.constants';

interface ContextType {
    setReportTitle: (title: string) => void;
    setReportDescText: (desc: string) => void;
}

const PRIMARY_FILTERS: FilterField[] = [
    { key: 'q', label: 'Name', type: 'text', placeholder: 'Search by name...' },
];

const STATUS_OPTIONS = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
];

const EXPANDED_FILTERS: FilterField[] = [
    { key: 'source', label: 'Report', type: 'select', options: SOURCE_OPTIONS },
    { key: 'cadence', label: 'Cadence', type: 'select', options: CADENCE_OPTIONS },
    { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
];

type FilterValues = { q: string; source: string; cadence: string; status: string };
const EMPTY_FILTERS: FilterValues = { q: '', source: '', cadence: '', status: '' };

export const ScheduledReportsListScreen: React.FC = () => {
    const navigate = useNavigate();
    const { setReportTitle, setReportDescText } = useOutletContext<ContextType>();
    const [items, setItems] = useState<ScheduledReportTypes.Model[]>([]);
    const [loading, setLoading] = useState(true);
    const [toDelete, setToDelete] = useState<ScheduledReportTypes.Model | null>(null);
    const [deleting, setDeleting] = useState(false);
    // Per-row guard so a rapid double-click can't fire two racing updates.
    const [togglingId, setTogglingId] = useState<string | null>(null);

    const [values, setValues] = useState<FilterValues>(EMPTY_FILTERS);
    const [applied, setApplied] = useState<FilterValues>(EMPTY_FILTERS);

    useEffect(() => {
        setReportTitle('Scheduled Reports');
        setReportDescText('Automated reports emailed to recipients you choose, on the cadence you set.');
    }, [setReportTitle, setReportDescText]);

    const load = useCallback(() => {
        setLoading(true);
        ScheduledReportService.fetchAll((isValid, msg) => {
            if (!isValid) toast(msg, { type: 'error', autoClose: 2500 });
        }).then((all) => {
            setItems(all);
            setLoading(false);
        });
    }, []);

    useEffect(() => { load(); }, [load]);

    // Schedules are few per subscription, so filtering is client-side.
    const filtered = useMemo(() => items.filter((r) => {
        if (applied.q && !r.name.toLowerCase().includes(applied.q.toLowerCase())) return false;
        if (applied.source && r.source !== applied.source) return false;
        if (applied.cadence && r.cadence !== applied.cadence) return false;
        if (applied.status === 'active' && !r.active) return false;
        if (applied.status === 'inactive' && r.active) return false;
        return true;
    }), [items, applied]);

    const appliedFilters = useMemo(() => {
        const pills: { key: string; label: string; onRemove: () => void }[] = [];
        const clear = (k: keyof FilterValues) => {
            const next = { ...applied, [k]: '' };
            setValues(next); setApplied(next);
        };
        if (applied.q) pills.push({ key: 'q', label: `Name: ${applied.q}`, onRemove: () => clear('q') });
        if (applied.source) pills.push({ key: 'source', label: `Report: ${SOURCE_LABELS[applied.source as ScheduledReportTypes.Source]}`, onRemove: () => clear('source') });
        if (applied.cadence) pills.push({ key: 'cadence', label: `Cadence: ${CADENCE_LABELS[applied.cadence as ScheduledReportTypes.Cadence]}`, onRemove: () => clear('cadence') });
        if (applied.status) pills.push({ key: 'status', label: `Status: ${applied.status === 'active' ? 'Active' : 'Inactive'}`, onRemove: () => clear('status') });
        return pills;
    }, [applied]);

    const toggleActive = async (report: ScheduledReportTypes.Model) => {
        if (togglingId) return; // a toggle is already in flight — ignore the click
        setTogglingId(report.id);
        // Optimistic flip; revert on failure.
        setItems((prev) => prev.map((r) => (r.id === report.id ? { ...r, active: !r.active } : r)));
        await ScheduledReportService.update(
            report.id,
            {
                name: report.name, source: report.source, window: report.window,
                cadence: report.cadence, recipients: report.recipients,
                timeOfDay: report.timeOfDay, timezone: report.timezone,
                dayOfWeek: report.dayOfWeek, dayOfMonth: report.dayOfMonth,
                active: !report.active,
            },
            (isValid, msg) => {
                if (!isValid) {
                    toast(msg, { type: 'error', autoClose: 2500 });
                    setItems((prev) => prev.map((r) => (r.id === report.id ? { ...r, active: report.active } : r)));
                }
            },
        );
        setTogglingId(null);
    };

    const onDelete = async () => {
        if (!toDelete) return;
        setDeleting(true);
        await ScheduledReportService.destroy(toDelete.id, (isValid, msg) => {
            setDeleting(false);
            setToDelete(null);
            toast(msg, { type: isValid ? 'success' : 'error', autoClose: 1800 });
            if (isValid) load();
        });
    };

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, mb: 1 }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/reports/scheduled-reports/create')}
                    sx={{ bgcolor: '#206FA5', '&:hover': { bgcolor: '#2B5A87' } }}
                >
                    Create New Report
                </Button>
            </Box>

            <Card sx={{ mt: 1 }}>
                <UnifiedFilterBar
                    primaryFilters={PRIMARY_FILTERS}
                    expandedFilters={EXPANDED_FILTERS}
                    values={values}
                    appliedFilters={appliedFilters}
                    onChange={(key, value) => setValues((prev) => ({ ...prev, [key]: value }))}
                    onApply={() => setApplied(values)}
                    onClear={() => { setValues(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); }}
                />
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {['Name', 'Report', 'Window', 'Cadence', 'Recipients', 'Next Run', 'Active', ''].map((h) => (
                                    <TableCell key={h || 'actions'}>
                                        <Typography variant="body2" sx={{ color: 'text.primary' }}>{h}</Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!loading && filtered.map((r) => (
                                <TableRow key={r.id} hover>
                                    <TableCell><Typography variant="body2">{r.name}</Typography></TableCell>
                                    <TableCell><Typography variant="body2" sx={{ color: 'text.secondary' }}>{SOURCE_LABELS[r.source] ?? r.source}</Typography></TableCell>
                                    <TableCell><Typography variant="body2" sx={{ color: 'text.secondary' }}>{WINDOW_LABELS[r.window] ?? r.window}</Typography></TableCell>
                                    <TableCell><Typography variant="body2" sx={{ color: 'text.secondary' }}>{CADENCE_LABELS[r.cadence] ?? r.cadence}</Typography></TableCell>
                                    <TableCell>
                                        <Tooltip title={(r.recipients ?? []).join(', ')}>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {(r.recipients ?? []).length} recipient{(r.recipients ?? []).length === 1 ? '' : 's'}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {formatInTimezone(r.nextRunAt, r.timezone)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Switch checked={r.active} onChange={() => toggleActive(r)} disabled={togglingId === r.id} size="small" />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => navigate(`/reports/scheduled-reports/${r.id}/edit`)}>
                                            <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => setToDelete(r)}>
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <When condition={loading}><Loading /></When>
                    <When condition={!loading && filtered.length < 1}>
                        <NoData
                            buttonText={null}
                            onClick={() => {}}
                            title={items.length === 0 ? 'No scheduled reports yet' : 'No reports match your filters'}
                            description={items.length === 0 ? 'Create one to start sending reports automatically.' : 'Try clearing or changing the filters above.'}
                        />
                    </When>
                </TableContainer>
            </Card>

            <AreYouSureModal
                title={'Delete this scheduled report?'}
                description={'It will stop sending. This cannot be undone.'}
                onHide={() => setToDelete(null)}
                loading={deleting}
                onConfirm={onDelete}
                visible={Boolean(toDelete)}
            />
        </>
    );
};

export default ScheduledReportsListScreen;
